/**
 * STRASSENHANDEL-Tab (Crime Ops)
 *
 * Items bleiben im Versteck/Lagerhaus.
 * Verkaufsaufträge verweisen auf Quelle — Hintergrund-Job zieht automatisch ab.
 * Erlaubte Waren: Zigaretten, Zigarren, Bier, Schnaps, Parfüm, Energydrinks, Nahrungsergänzung.
 */

import { api, ApiError } from '../../services/api.js';
import { showToast } from '../../utils/toast.js';
import { refreshBalance } from '../../utils/refreshBalance.js';
import { formatPropertyDisplayName } from '../../utils/property-select.js';

// ─── Typen ────────────────────────────────────────────────────────────────────

interface StallItem {
  id:                  string;
  itemKey:             string;
  itemType:            string;
  quantity:            number;
  pricePerUnit:        string;
  availableInProperty: number;
  sourcePropertyId:    string;
  sourcePropertyLabel: string;
}

interface Stall {
  id:       string;
  level:    number;
  slots:    number;
  earnings: string;
  items:    StallItem[];
}

interface PropertyInventoryEntry {
  itemType: string;
  itemKey:  string;
  quantity: number;
}

interface PropertySource {
  id:             string;
  subType:        string;
  label:          string;
  customName:     string | null;
  nickname:       string | null;
  inventoryItems: PropertyInventoryEntry[];
}

interface PropertiesResponse {
  warehouses: PropertySource[];
  hideouts:   PropertySource[];
}

// ─── Erlaubte Waren (spiegelt street-market.constants.ts) ────────────────────

const ROLLING_PREFIXES   = ['zigarettenschachtel_', 'zigarrenkiste_', 'zigarette_', 'zigarre_'] as const;
const LEGAL_LABOR_KEYS   = new Set([
  'output_bier', 'output_schnaps', 'output_nahrungsergaenzung',
  'output_parfuem', 'output_energydrink',
]);

function isStreetMarketItem(itemType: string, itemKey: string): boolean {
  if (itemType === 'ROLLING_OUTPUT') return ROLLING_PREFIXES.some((p) => itemKey.startsWith(p));
  if (itemType === 'LABOR_OUTPUT')   return LEGAL_LABOR_KEYS.has(itemKey);
  return false;
}

// ─── Stall-Level-Konstanten ───────────────────────────────────────────────────

const STALL_LEVELS: Record<number, { slots: number; upgradeCostNox: number }> = {
  1: { slots: 1,  upgradeCostNox: 0 },
  2: { slots: 2,  upgradeCostNox: 500000 },
  3: { slots: 4,  upgradeCostNox: 1500000 },
  4: { slots: 7,  upgradeCostNox: 4000000 },
  5: { slots: 12, upgradeCostNox: 10000000 },
};

const QUALITY_LABELS: Record<number, { label: string; cssVar: string }> = {
  1: { label: 'AUSSCHUSS',      cssVar: '--text-muted'    },
  2: { label: 'HINTERHOF-WARE', cssVar: '--accent-red'    },
  3: { label: 'MARKTWARE',      cssVar: '--accent-orange' },
  4: { label: 'FEINWARE',       cssVar: '--accent-green'  },
  5: { label: 'PREMIUM-ERNTE',  cssVar: '--accent-gold'   },
};

const QUALITY_MODIFIER: Record<number, number> = {
  1: 1.00, 2: 1.20, 3: 1.40, 4: 1.70, 5: 2.00,
};

// ─── State ────────────────────────────────────────────────────────────────────

let isMounted   = false;
let stallData:    Stall | null = null;
let allProps:     PropertySource[] = [];
let clickHandler: ((e: Event) => void) | null = null;

// ─── Helfer ───────────────────────────────────────────────────────────────────

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function fmtNox(centsStr: string | number): string {
  const n = typeof centsStr === 'number' ? centsStr : parseInt(centsStr, 10);
  return (n / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₦';
}

function extractQualityTier(itemKey: string): number {
  const m = /q([1-5])$/.exec(itemKey);
  return m ? parseInt(m[1] ?? '1', 10) : 1;
}

function itemDisplayName(itemKey: string): string {
  const titleCase = (s: string): string =>
    s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  let m: RegExpExecArray | null;
  if ((m = /^zigarettenschachtel_([a-z0-9_]+)_q([1-5])$/.exec(itemKey)))
    return `ZIGARETTENSCHACHTEL ${titleCase(m[1] ?? '').toUpperCase()}`;
  if ((m = /^zigarrenkiste_([a-z0-9_]+)_q([1-5])$/.exec(itemKey)))
    return `ZIGARRENKISTE ${titleCase(m[1] ?? '').toUpperCase()}`;
  if (itemKey.startsWith('output_'))
    return itemKey.replace(/^output_/, '').replace(/_/g, ' ').toUpperCase();
  return itemKey.toUpperCase().replace(/_/g, ' ');
}

function qualityBadge(tier: number): string {
  const def = QUALITY_LABELS[tier] ?? { label: `Q${String(tier)}`, cssVar: '--text-muted' };
  return `<span class="sm-q-badge" style="color:var(${esc(def.cssVar)});border-color:var(${esc(def.cssVar)});">${esc(def.label)}</span>`;
}

function basePriceFromQualityPrice(pricePerUnit: string, tier: number): string {
  const modifier  = QUALITY_MODIFIER[tier] ?? 1;
  const baseCents = Math.round(parseInt(pricePerUnit, 10) / modifier);
  return fmtNox(baseCents);
}

// ─── Styles ───────────────────────────────────────────────────────────────────

function addStyles(): void {
  const id = 'streetmarket-tab-styles';
  let el = document.getElementById(id) as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement('style');
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = `
    #sm-root { font-family: var(--font-mono); }
    .sm-section { margin-bottom: var(--space-4); }
    .sm-label {
      font-size: var(--font-size-xs); color: var(--text-muted);
      letter-spacing: 0.10em; text-transform: uppercase;
      margin: 0 0 var(--space-2) 0;
    }
    .sm-panel {
      background: var(--bg-elevated); border: 1px solid var(--border);
      border-radius: var(--radius); padding: var(--space-3);
      margin-bottom: var(--space-3);
    }
    .sm-panel-header {
      display: flex; justify-content: space-between; align-items: center;
      margin-bottom: var(--space-2);
    }
    .sm-panel-title { font-size: var(--font-size-sm); color: var(--text-primary); }
    .sm-earnings-amount { font-size: var(--font-size-sm); color: var(--accent-gold); font-weight: bold; }
    .sm-btn {
      font-family: var(--font-mono); font-size: var(--font-size-xs);
      background: transparent; border: 1px solid var(--border);
      border-radius: var(--radius-btn); padding: 5px 14px;
      cursor: pointer; letter-spacing: 0.06em; color: var(--text-muted);
    }
    .sm-btn--collect { border-color: var(--accent-gold); color: var(--accent-gold); }
    .sm-btn--instant { border-color: var(--accent-orange); color: var(--accent-orange); }
    .sm-btn--remove  { border-color: var(--accent-red); color: var(--accent-red); }
    .sm-btn--upgrade { border-color: var(--accent-green); color: var(--accent-green); }
    .sm-btn--add     { border-color: var(--accent-blue); color: var(--accent-blue); }
    .sm-btn--max     { border-color: var(--accent-gold); color: var(--accent-gold); }
    .sm-btn:disabled { opacity: 0.35; cursor: default; }
    .sm-qty-input {
      font-family: var(--font-mono); font-size: var(--font-size-xs);
      background: var(--bg-surface); color: var(--text-primary);
      border: 1px solid var(--border); border-radius: var(--radius);
      padding: 5px 8px; -moz-appearance: textfield;
    }
    .sm-qty-input::-webkit-inner-spin-button,
    .sm-qty-input::-webkit-outer-spin-button { -webkit-appearance: none; }
    .sm-slot {
      background: var(--bg-surface); border: 1px solid var(--border);
      border-radius: var(--radius); padding: var(--space-3); margin-bottom: var(--space-2);
    }
    .sm-slot--empty {
      border-style: dashed; color: var(--text-muted); font-size: var(--font-size-xs);
      padding: var(--space-2) var(--space-3); text-align: center;
    }
    .sm-slot-name { font-size: var(--font-size-sm); color: var(--text-primary); margin-bottom: var(--space-1); }
    .sm-slot-meta {
      font-size: var(--font-size-xs); color: var(--text-muted);
      margin-bottom: var(--space-2); display: grid;
      grid-template-columns: 1fr 1fr; gap: 2px 8px;
    }
    .sm-slot-meta-val { color: var(--text-primary); }
    .sm-slot-meta-val--warn { color: var(--accent-red); }
    .sm-slot-actions { display: flex; gap: var(--space-2); flex-wrap: wrap; }
    .sm-slot-source {
      font-size: var(--font-size-xs); color: var(--text-muted);
      margin-bottom: var(--space-1); letter-spacing: 0.05em;
    }
    .sm-q-badge {
      font-size: var(--font-size-xs); letter-spacing: 0.06em;
      padding: 2px 8px; border-radius: var(--radius-sm);
      border: 1px solid; display: inline-block; margin-left: var(--space-1);
    }
    .sm-upgrade-panel {
      background: var(--bg-elevated); border: 1px solid var(--border);
      border-radius: var(--radius); padding: var(--space-3); margin-bottom: var(--space-3);
    }
    .sm-upgrade-title { font-size: var(--font-size-sm); color: var(--text-primary); margin-bottom: var(--space-2); }
    .sm-upgrade-meta  { font-size: var(--font-size-xs); color: var(--text-muted); margin-bottom: var(--space-2); line-height: 1.6; }
    .sm-upgrade-sep   { border: none; border-top: 1px solid var(--border); margin: var(--space-2) 0; }
    .sm-add-form {
      background: var(--bg-elevated); border: 1px solid var(--border);
      border-radius: var(--radius); padding: var(--space-3); margin-bottom: var(--space-3);
    }
    .sm-form-row { display: flex; gap: var(--space-2); flex-wrap: wrap; align-items: flex-end; margin-bottom: var(--space-2); }
    .sm-form-group { display: flex; flex-direction: column; gap: 4px; }
    .sm-select {
      font-family: var(--font-mono); font-size: var(--font-size-xs);
      background: var(--bg-surface); color: var(--text-primary);
      border: 1px solid var(--border); border-radius: var(--radius);
      padding: 5px 8px;
    }
    .sm-empty { color: var(--text-muted); font-size: var(--font-size-xs); padding: var(--space-2) 0; }
    .sm-error { color: var(--accent-red); font-size: var(--font-size-xs); margin-top: var(--space-1); }
    /* Shell = .modal-backdrop/.modal aus dashboard.css (siehe components/BottomSheet.ts). */
    .sm-confirm-body { font-size: var(--font-size-xs); color: var(--text-muted); line-height: 1.7; }
  `;
}

// ─── Render ───────────────────────────────────────────────────────────────────

function renderStallSlots(stall: Stall): string {
  const filled = stall.items.map((item) => {
    const tier       = extractQualityTier(item.itemKey);
    const qtierDef   = QUALITY_LABELS[tier] ?? { label: 'Q?', cssVar: '--text-muted' };
    const modifier   = QUALITY_MODIFIER[tier] ?? 1;
    const bonusPct   = Math.round((modifier - 1) * 100);
    const baseStr    = basePriceFromQualityPrice(item.pricePerUnit, tier);
    const hasQuality = item.itemType === 'ROLLING_OUTPUT';
    const isLow      = item.availableInProperty === 0;
    const availClass = isLow ? 'sm-slot-meta-val--warn' : 'sm-slot-meta-val';

    return `
      <div class="sm-slot">
        <div class="sm-slot-source">QUELLE: ${esc(item.sourcePropertyLabel.toUpperCase())}</div>
        <div class="sm-slot-name">
          ${esc(itemDisplayName(item.itemKey))}
          ${hasQuality ? qualityBadge(tier) : ''}
        </div>
        <div class="sm-slot-meta">
          <span>PREIS/STK.</span>
          <span class="sm-slot-meta-val">${esc(fmtNox(item.pricePerUnit))}</span>
          ${hasQuality ? `
          <span>QUALITÄTSBONUS</span>
          <span class="sm-slot-meta-val" style="color:var(${esc(qtierDef.cssVar)});">+${String(bonusPct)}% (Q${String(tier)})</span>
          <span>BASISPREIS</span>
          <span class="sm-slot-meta-val">${esc(baseStr)}/STK.</span>
          ` : ''}
          <span>AUFTRAG</span>
          <span class="sm-slot-meta-val">${String(item.quantity)} STK.</span>
          <span>IM LAGER</span>
          <span class="${availClass}">${isLow ? 'LEER' : String(item.availableInProperty) + ' STK.'}</span>
        </div>
        <div class="sm-slot-actions">
          <button class="sm-btn sm-btn--instant" data-sm-instant="${esc(item.id)}" ${isLow ? 'disabled title="Kein Lagerbestand"' : ''}>SOFORTVERKAUF</button>
          <button class="sm-btn sm-btn--remove"  data-sm-remove="${esc(item.id)}">AUFTRAG AUFHEBEN</button>
        </div>
      </div>`;
  }).join('');

  const emptyCount = Math.max(0, stall.slots - stall.items.length);
  const empty = Array.from({ length: emptyCount }, (_, i) =>
    `<div class="sm-slot sm-slot--empty">SLOT ${String(stall.items.length + i + 1)} — FREI</div>`,
  ).join('');

  return filled + empty;
}

function buildSellableItems(): { propId: string; propLabel: string; itemKey: string; itemType: string; quantity: number }[] {
  const result: { propId: string; propLabel: string; itemKey: string; itemType: string; quantity: number }[] = [];
  for (const prop of allProps) {
    // NT-POLISH-01 (P1): "Eigenname — Typ" zentral (Kategorie ist hier nicht relevant).
    const label = formatPropertyDisplayName({ id: prop.id, label: prop.label, subType: prop.subType, customName: prop.customName, nickname: prop.nickname, isHideout: false });
    for (const inv of prop.inventoryItems) {
      if (isStreetMarketItem(inv.itemType, inv.itemKey) && inv.quantity > 0) {
        result.push({ propId: prop.id, propLabel: label, itemKey: inv.itemKey, itemType: inv.itemType, quantity: inv.quantity });
      }
    }
  }
  return result;
}

function isRestockForExistingOrder(stall: Stall, propId: string, itemKey: string, itemType: string): boolean {
  return stall.items.some(
    (i) => i.itemKey === itemKey && i.itemType === itemType && i.sourcePropertyId === propId,
  );
}

function renderAddForm(stall: Stall): string {
  const slotsFull = stall.items.length >= stall.slots;
  const allItems   = buildSellableItems();
  // Bei vollen Slots ist nur noch Nachschub für exakt dasselbe Item (gleiche Quelle,
  // gleiche Qualität) möglich — das verbraucht keinen neuen Slot (siehe addItemToStall).
  const items = slotsFull
    ? allItems.filter((i) => isRestockForExistingOrder(stall, i.propId, i.itemKey, i.itemType))
    : allItems;

  const title = slotsFull ? 'NACHSCHUB LIEFERN' : 'VERKAUFSAUFTRAG ERSTELLEN';

  if (items.length === 0) {
    const emptyText = slotsFull
      ? `Alle ${String(stall.slots)} Slots belegt. Nachschub ist nur für bereits gelistete Waren aus derselben Quelle möglich — davon ist aktuell nichts auf Lager.`
      : `Keine verkaufbaren Waren in Verstecken oder Lagern.<br>
      Benötigt: Zigarettenschachteln, Zigarrenkisten, Bier, Schnaps, Parfüm, Energydrinks oder Nahrungsergänzung.`;
    return `<div class="sm-add-form">
      <p class="sm-label">${title}</p>
      <p class="sm-empty">${emptyText}</p>
    </div>`;
  }

  const options = items.map((i) => {
    const tier   = extractQualityTier(i.itemKey);
    const name   = itemDisplayName(i.itemKey);
    const qLabel = i.itemType === 'ROLLING_OUTPUT' ? ` Q${String(tier)}` : '';
    return `<option value="${esc(i.propId)}::${esc(i.itemKey)}::${esc(i.itemType)}::${String(i.quantity)}">
      ${esc(name)}${qLabel} (${String(i.quantity)} STK.) — ${esc(i.propLabel)}
    </option>`;
  }).join('');

  const hint = slotsFull
    ? `Alle ${String(stall.slots)} Slots belegt — nur Nachschub für bereits gelistete Waren aus derselben Quelle.`
    : 'Ware bleibt im Versteck/Lager — wird automatisch beim Verkauf abgezogen.';
  const buttonLabel = slotsFull ? 'NACHLIEFERN' : 'INSERIEREN';

  return `
    <div class="sm-add-form">
      <p class="sm-label">${title}</p>
      <p style="font-size:var(--font-size-xs);color:var(--text-muted);margin:0 0 var(--space-2);">
        ${hint}
      </p>
      <div class="sm-form-row">
        <div class="sm-form-group">
          <label class="sm-label" style="margin:0;">WARE + QUELLE</label>
          <select class="sm-select" id="sm-add-item-select" style="min-width:220px;">${options}</select>
        </div>
        <div class="sm-form-group">
          <label class="sm-label" style="margin:0;">MENGE</label>
          <div style="display:flex;gap:var(--space-2);">
            <input type="number" class="sm-qty-input" id="sm-add-qty" min="1" value="1" style="width:80px;">
            <button class="sm-btn sm-btn--max" data-sm-add-max type="button">MAX</button>
          </div>
        </div>
        <button class="sm-btn sm-btn--add" data-sm-add>${buttonLabel}</button>
      </div>
      <p id="sm-add-error" class="sm-error" style="display:none;"></p>
    </div>`;
}

function renderUpgrade(stall: Stall): string {
  const nextLevel = stall.level + 1;
  if (nextLevel > 5) {
    return `<div class="sm-upgrade-panel">
      <p class="sm-upgrade-title">VERKAUFSSTAND STUFE 5 / 5</p>
      <p class="sm-upgrade-meta">Maximale Stufe erreicht.</p>
    </div>`;
  }
  const nextDef = STALL_LEVELS[nextLevel];
  if (!nextDef) return '';
  const gainSlots = nextDef.slots - stall.slots;

  return `
    <div class="sm-upgrade-panel">
      <p class="sm-upgrade-title">VERKAUFSSTAND STUFE ${String(stall.level)} / 5</p>
      <p class="sm-upgrade-meta">AKTUELLE SLOTS: ${String(stall.slots)}</p>
      <hr class="sm-upgrade-sep">
      <p class="sm-upgrade-meta">
        UPGRADE AUF STUFE ${String(nextLevel)}:<br>
        +${String(gainSlots)} ZUSÄTZLICHE SLOTS (GESAMT: ${String(nextDef.slots)})<br>
        KOSTEN: ${esc(fmtNox(nextDef.upgradeCostNox))}
      </p>
      <button class="sm-btn sm-btn--upgrade" data-sm-upgrade>UPGRADEN</button>
    </div>`;
}

function renderInner(): string {
  if (!stallData) return '<p class="sm-empty">LADE...</p>';

  const stall      = stallData;
  const earnings   = parseInt(stall.earnings, 10);
  const hasEarnings = earnings > 0;

  return `
    <div class="sm-section">
      <div class="sm-panel">
        <div class="sm-panel-header">
          <span class="sm-panel-title">EINNAHMEN AUSSTEHEND</span>
          <span class="sm-earnings-amount">${esc(fmtNox(earnings))}</span>
        </div>
        <button class="sm-btn sm-btn--collect" data-sm-collect ${!hasEarnings ? 'disabled' : ''}>
          EINNAHMEN EINSAMMELN
        </button>
      </div>
    </div>

    <div class="sm-section">
      <p class="sm-label">AKTIVE AUFTRÄGE (${String(stall.items.length)} / ${String(stall.slots)} SLOTS)</p>
      ${renderStallSlots(stall)}
    </div>

    <div class="sm-section">
      ${renderAddForm(stall)}
    </div>

    <div class="sm-section">
      ${renderUpgrade(stall)}
    </div>`;
}

export function renderStreetMarketTab(): string {
  addStyles();
  return `<div id="sm-root">${renderInner()}</div>`;
}

// ─── Mount / Cleanup ─────────────────────────────────────────────────────────

export async function mountStreetMarketTab(): Promise<void> {
  if (isMounted) return;
  isMounted = true;
  await loadData();
  rerender();
}

export function cleanupStreetMarketTab(): void {
  isMounted = false;
  const root = document.getElementById('sm-root');
  if (root && clickHandler) root.removeEventListener('click', clickHandler);
  clickHandler = null;
}

export async function refreshStreetMarketTab(): Promise<void> {
  await loadData();
}

// ─── Daten laden ─────────────────────────────────────────────────────────────

async function loadData(): Promise<void> {
  try {
    const [stallRes, propsRes] = await Promise.all([
      api.get<Stall>('/streetmarket/stall'),
      api.get<PropertiesResponse>('/properties'),
    ]);
    stallData = stallRes;
    allProps  = [...propsRes.hideouts, ...propsRes.warehouses];
  } catch {
    stallData = null;
  }
}

// ─── Event-Handling ───────────────────────────────────────────────────────────

function rerender(): void {
  const root = document.getElementById('sm-root');
  if (!root) return;
  root.innerHTML = renderInner();
  attachListeners();
}

function attachListeners(): void {
  const root = document.getElementById('sm-root');
  if (!root) return;

  if (clickHandler) root.removeEventListener('click', clickHandler);

  clickHandler = (e: Event) => {
    const target = e.target as Element;

    if (target.closest('[data-sm-collect]')) {
      void handleCollect();
      return;
    }

    const instantBtn = target.closest<HTMLButtonElement>('[data-sm-instant]');
    if (instantBtn?.dataset.smInstant) {
      handleInstantSell(instantBtn.dataset.smInstant);
      return;
    }

    const removeBtn = target.closest<HTMLButtonElement>('[data-sm-remove]');
    if (removeBtn?.dataset.smRemove) {
      void handleRemove(removeBtn.dataset.smRemove);
      return;
    }

    if (target.closest('[data-sm-add]')) {
      void handleAdd();
      return;
    }

    if (target.closest('[data-sm-add-max]')) {
      handleAddMax();
      return;
    }

    if (target.closest('[data-sm-upgrade]')) {
      void handleUpgrade();
      return;
    }

    const confirmBtn = target.closest<HTMLButtonElement>('[data-sm-confirm-instant]');
    if (confirmBtn?.dataset.smConfirmInstant) {
      void executeInstantSell(confirmBtn.dataset.smConfirmInstant);
      document.getElementById('sm-confirm-overlay')?.remove();
      return;
    }

    if (target.closest('[data-sm-cancel]')) {
      document.getElementById('sm-confirm-overlay')?.remove();
      return;
    }
  };

  root.addEventListener('click', clickHandler);
}

// ─── Handler ─────────────────────────────────────────────────────────────────

async function handleCollect(): Promise<void> {
  try {
    const res = await api.post<{ collectedCents: string }>('/streetmarket/stall/collect', {});
    showToast(`${fmtNox(res.collectedCents)} eingesammelt.`, 'info');
    void refreshBalance();
    await loadData();
    rerender();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Fehler beim Einsammeln.', 'error');
  }
}

function handleInstantSell(itemId: string): void {
  const item = stallData?.items.find((i) => i.id === itemId);
  if (!item) return;

  const tier       = extractQualityTier(item.itemKey);
  const hasQuality = item.itemType === 'ROLLING_OUTPUT';
  const sellQty    = Math.min(item.availableInProperty, item.quantity);

  const overlay = document.createElement('div');
  overlay.id = 'sm-confirm-overlay';
  overlay.className = 'modal-backdrop';
  overlay.innerHTML = `
    <div class="modal modal--compact" role="dialog" aria-modal="true" aria-label="Sofortverkauf">
      <div class="modal-header"><h2>SOFORTVERKAUF – GROSSABNEHMER</h2></div>
      <div class="modal-body">
        <p class="sm-confirm-body">
          WARE: ${esc(itemDisplayName(item.itemKey))} ${hasQuality ? qualityBadge(tier) : ''}<br>
          MENGE (VERFÜGBAR): ${String(sellQty)} STK.<br>
          PREIS: ${esc(fmtNox(item.pricePerUnit))}/STK.<br>
          RABATT: 20–30% (zufällig, vom Großabnehmer)<br>
          EINNAHMEN GEHEN SOFORT AUF IHR KONTO.
        </p>
      </div>
      <div class="modal-actions">
        <button class="sm-btn sm-btn--instant" data-sm-confirm-instant="${esc(itemId)}">SOFORT VERKAUFEN</button>
        <button class="sm-btn" data-sm-cancel>ABBRECHEN</button>
      </div>
    </div>`;
  document.body.appendChild(overlay);
  if (clickHandler !== null) overlay.addEventListener('click', clickHandler);
}

async function executeInstantSell(itemId: string): Promise<void> {
  try {
    const res = await api.post<{ soldQty: number; totalCents: string; discountPct: number; pricePerUnit: string }>(
      `/streetmarket/stall/sell-instant/${encodeURIComponent(itemId)}`,
      {},
    );
    showToast(
      `${fmtNox(res.totalCents)} verdient (${String(res.discountPct)}% Rabatt, ${String(res.soldQty)} STK. à ${fmtNox(res.pricePerUnit)})`,
      'info',
    );
    void refreshBalance();
    await loadData();
    rerender();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Fehler beim Sofortverkauf.', 'error');
  }
}

async function handleRemove(itemId: string): Promise<void> {
  try {
    await api.delete<null>(`/streetmarket/stall/items/${encodeURIComponent(itemId)}`);
    showToast('Verkaufsauftrag aufgehoben.', 'info');
    await loadData();
    rerender();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Fehler beim Aufheben.', 'error');
  }
}

function handleAddMax(): void {
  const selectEl = document.getElementById('sm-add-item-select') as HTMLSelectElement | null;
  const qtyEl    = document.getElementById('sm-add-qty') as HTMLInputElement | null;
  if (!selectEl || !qtyEl) return;

  const maxQty = parseInt(selectEl.value.split('::')[3] ?? '1', 10);
  qtyEl.value  = String(maxQty);
}

async function handleAdd(): Promise<void> {
  const errEl    = document.getElementById('sm-add-error');
  if (errEl) errEl.style.display = 'none';

  const selectEl = document.getElementById('sm-add-item-select') as HTMLSelectElement | null;
  const qtyEl    = document.getElementById('sm-add-qty') as HTMLInputElement | null;
  if (!selectEl || !qtyEl) return;

  const parts          = selectEl.value.split('::');
  const sourcePropertyId = parts[0] ?? '';
  const itemKey          = parts[1] ?? '';
  const maxQty           = parseInt(parts[3] ?? '1', 10);
  const qty              = Math.min(parseInt(qtyEl.value, 10), maxQty);

  if (!sourcePropertyId || !itemKey || qty < 1) {
    if (errEl) { errEl.textContent = 'Ungültige Eingabe.'; errEl.style.display = ''; }
    return;
  }

  const isRestock = stallData ? isRestockForExistingOrder(stallData, sourcePropertyId, itemKey, parts[2] ?? '') : false;

  try {
    await api.post<unknown>('/streetmarket/stall/items', { itemKey, quantity: qty, sourcePropertyId });
    showToast(
      isRestock
        ? `${String(qty)} STK. Nachschub geliefert.`
        : `Verkaufsauftrag für ${String(qty)} STK. erstellt.`,
      'info',
    );
    await loadData();
    rerender();
  } catch (err) {
    const msg = err instanceof ApiError ? err.message : 'Fehler beim Erstellen.';
    if (errEl) { errEl.textContent = msg; errEl.style.display = ''; }
  }
}

async function handleUpgrade(): Promise<void> {
  try {
    const updated = await api.post<Stall>('/streetmarket/stall/upgrade', {});
    stallData = updated;
    showToast(`Stand auf Stufe ${String(updated.level)} geupgradet (${String(updated.slots)} Slots).`, 'info');
    void refreshBalance();
    rerender();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Fehler beim Upgrade.', 'error');
  }
}
